import * as focusTrap from 'focus-trap';

const menu = () => {
  const localizedVariables = window.acato_variables || {};
  const dropdownToggles = document.querySelectorAll( '.menu__submenu-toggle-button' );
  const allSubmenus = document.querySelectorAll( '.menu__submenu' );
  const headerNavWrap = document.querySelector( '.header__main-nav--inline' );
  const headerNav = document.querySelector( '.header__nav' );
  const headerCtasDesktop = document.querySelector( '.header__ctas-wrap.large > .header__ctas' );
  const topbarMenuLeftDesktop = document.querySelector( '.top-bar .top-bar-menu.left' );
  const topbarMenuRightDesktop = document.querySelector( '.top-bar .top-bar-menu.right' );
  const mobileMenuToggle = document.querySelector( '.responsive-toggle--inline' );
  let mobileView = mobileMenuToggle ? window.getComputedStyle( mobileMenuToggle ).display !== 'none' : false;

  /**
   * Opens a specific dropdown menu.
   *
   * This function ensures that the dropdown menu associated with the provided toggle button
   * is opened. It first checks if the dropdown is already open, and if so, exits early.
   * Otherwise, it closes all other open dropdowns, updates the `aria-expanded` attribute
   * of the toggle button to `true`, sets the `aria-hidden` attribute of the dropdown menu
   * to `false`, and adds the `is-open` class to the dropdown menu to visually display it.
   *
   * @param {HTMLElement} toggle - The toggle button associated with the dropdown menu to be opened.
   */
  const openDropdown = toggle => {
    const dropdown = toggle.nextElementSibling;
    const isOpen = toggle.getAttribute( 'aria-expanded' ) === 'true';

    if (isOpen) {
      return;
    }

    closeAllDropdowns();

    if (mobileView) {
      handleMobileMenuKeyboardUpDown( dropdown, 'a, button' );
      headerNav.removeEventListener( 'keydown', handleMobileMenuKeyboardUpDown );
    }

    toggle.setAttribute( 'aria-expanded', true );
    dropdown.setAttribute( 'aria-hidden', false );
    dropdown.classList.add( 'is-open' );
  };

  /**
   * Closes all open dropdown menus.
   *
   * This function iterates over all dropdown toggle buttons and ensures that
   * their associated dropdown menus are closed. It updates the `aria-expanded`
   * attribute of each toggle button to `false`, sets the `aria-hidden` attribute
   * of the dropdown menus to `true`, and removes the `is-open` class from the
   * dropdown menus to visually hide them.
   */
  const closeDropdown = toggle => {
    const dropdown = toggle.nextElementSibling;
    const isClosed = toggle.getAttribute( 'aria-expanded' ) === 'false';

    if (isClosed) {
      return;
    }

    if (mobileView) {
      handleMobileMenuKeyboardUpDown( headerNavWrap, '.menu__item' );
    }

    toggle.setAttribute( 'aria-expanded', false );
    dropdown.setAttribute( 'aria-hidden', true );
    dropdown.classList.remove( 'is-open' );
  };

  /**
   * Closes all open dropdown menus.
   *
   * This function iterates over all dropdown toggle buttons and ensures that
   * their associated dropdown menus are closed. It updates the `aria-expanded`
   * attribute of each toggle button to `false`, sets the `aria-hidden` attribute
   * of the dropdown menus to `true`, and removes the `is-open` class from the
   * dropdown menus to visually hide them.
   */
  const closeAllDropdowns = () => {
    [...dropdownToggles].forEach( toggle => {
      const dropdown = toggle.nextElementSibling;
      toggle.setAttribute( 'aria-expanded', false );
      dropdown.setAttribute( 'aria-hidden', true );
      dropdown.classList.remove( 'is-open' );
    } );

    closeHeaderTranslate();
  };

  /**
   * Closes the header translate overlay.
   */
  const closeHeaderTranslate = () => {
    const headerTranslate = document.querySelector( '.header__translate' );
    const toggleHeaderTranslate = document.querySelector( '[data-toggle-translate]' );

    headerTranslate.classList.remove( 'open' );
    toggleHeaderTranslate.setAttribute( 'aria-expanded', false );
  }

  /**
   * Binds dropdown toggle functionality for menu items with submenus.
   *
   * This function sets up event listeners for dropdown toggle buttons, enabling
   * users to open and close dropdown menus. It also manages focus trapping within
   * the dropdowns and handles interactions with back buttons and close buttons.
   */
  const bindDropdownToggles = () => {
    const backButtons = document.querySelectorAll( '.menu__submenu-item--back' );

    // Save all focus trap instances in a list, this way we can manipulate them even outside their scope.
    const allFocusTraps = [];

    [...dropdownToggles].forEach( toggle => {
      const dropdown = toggle.nextElementSibling;
      const closeButtonInDropdown = dropdown.querySelector( '.menu__submenu-close' );
      const focusTrapInstance = focusTrap.createFocusTrap( dropdown, {
        // Do all the dropdown logic on (de)activate so we can just (de)activate the focus trap when needed.
        onActivate: () => {
          openDropdown( toggle );

          if (mobileView) {
            const backButton = dropdown.querySelector( '.menu__submenu-item--back' );
            setTimeout( () => {
              backButton.focus();
            }, 100 );
          }
        },
        onDeactivate: () => {
          closeDropdown( toggle );
        },
        checkCanFocusTrap: (trapContainers) => {
          const results = trapContainers.map( (trapContainer) => {
            return new Promise( (resolve) => {
              const interval = setInterval( () => {
                if (getComputedStyle( trapContainer ).visibility !== 'hidden') {
                  resolve();
                  clearInterval( interval );
                }
              }, 5 );
            } );
          } );
          return Promise.all( results );
        },
        initialFocus: toggle,
        fallbackFocus: toggle,
        escapeDeactivates: true,
        allowOutsideClick: true,
      } );

      allFocusTraps.push( focusTrapInstance );

      // When clicked on a toggle, close all other dropdowns and toggle the current one.
      toggle.addEventListener( 'click', e => {
        e.preventDefault();
        const currentIsOpen = toggle.getAttribute( 'aria-expanded' ) === 'true';

        allFocusTraps.forEach( trap => trap.deactivate() );

        const index = [...dropdownToggles].indexOf( toggle );
        const currentFocusTrap = allFocusTraps[index];

        if (currentIsOpen) {
          currentFocusTrap.deactivate();
        } else {
          currentFocusTrap.activate();
        }
      } );

      // When clicking outside the dropdown or toggle, close it.
      document.addEventListener( 'click', e => {
        if (!dropdown.contains( e.target ) && !toggle.contains( e.target )) {
          focusTrapInstance.deactivate();
          closeDropdown( toggle );
        }
      } );

      // If there's a close button in the dropdown, bind it to close the dropdown and deactivate the focus trap.
      if (closeButtonInDropdown) {
        closeButtonInDropdown.addEventListener( 'click', e => {
          e.preventDefault();
          focusTrapInstance.deactivate();
          closeDropdown( toggle );
        } );
      }

      // If there's a back button in the dropdown (mobile view), bind it to close the dropdown and deactivate the focus trap.
      if (backButtons && mobileView) {
        [...backButtons].forEach( button => {
          button.addEventListener( 'click', () => {
            const parentMenu = button.closest( '.menu__submenu' );
            const parentItem = parentMenu.closest( '.menu__item--has-submenu' );
            const parentToggle = parentItem.querySelector( '.menu__submenu-toggle-button' );

            parentToggle.focus();
            focusTrapInstance.deactivate();
            closeDropdown( parentToggle );
          } );
        } );
      }
    } );
  };

  /**
   * Binds the mobile menu toggle functionality.
   *
   * This function sets up the behavior for the mobile menu toggle button, allowing users
   * to open and close the mobile menu. It also ensures proper focus management using a
   * focus trap and handles keyboard interactions, such as closing the menu with the Escape key.
   */
  const bindMobileMenuToggle = () => {
    if (mobileMenuToggle) {
      const mobileMainMenuFocusTrap = focusTrap.createFocusTrap( headerNavWrap, {
        checkCanFocusTrap: (trapContainers) => {
          const results = trapContainers.map( (trapContainer) => {
            return new Promise( (resolve) => {
              const interval = setInterval( () => {
                if (getComputedStyle( trapContainer ).visibility !== 'hidden') {
                  resolve();
                  clearInterval( interval );
                }
              }, 5 );
            } );
          } );
          return Promise.all( results );
        },
        initialFocus: mobileMenuToggle,
        fallbackFocus: mobileMenuToggle,
        escapeDeactivates: true,
        clickOutsideDeactivates: true,
      } );

      mobileMenuToggle.addEventListener( 'click', () => {
        closeHeaderTranslate();
        headerNavWrap.classList.toggle( 'is-open' );
        mobileMenuToggle.setAttribute( 'aria-expanded', headerNavWrap.classList.contains( 'is-open' ) );

        if (headerNavWrap.classList.contains( 'is-open' )) {
          mobileMainMenuFocusTrap.activate();

          if (mobileView) {
            mobileMenuToggle.setAttribute( 'aria-label', localizedVariables?.hide_main_menu_text || 'Open mobile menu' );
          }
        } else {
          mobileMainMenuFocusTrap.deactivate();

          if (mobileView) {
            mobileMenuToggle.setAttribute( 'aria-label', localizedVariables?.show_main_menu_text || 'Open mobile menu' );
          }
        }
      } );

      document.addEventListener( 'keydown', e => {
        // Only close the mobile menu if the Escape key is pressed and no submenus are open, submenus have their own Escape key handling.
        if (
          e.key === 'Escape'
          && headerNavWrap.classList.contains( 'is-open' )
          && !Array.from( allSubmenus ).some( submenu => submenu.classList.contains( 'is-open' ) )
        ) {
          headerNavWrap.classList.remove( 'is-open' );
          mobileMenuToggle.setAttribute( 'aria-expanded', false );
          mobileMainMenuFocusTrap.deactivate();
        }
      } );

      const mobileCloseButtons = document.querySelectorAll( '.menu__mobile-close' );

      [...mobileCloseButtons].forEach( button => {
        button.addEventListener( 'click', () => {
          if (headerNavWrap.classList.contains( 'is-open' )) {
            headerNavWrap.classList.remove( 'is-open' );
            mobileMenuToggle.setAttribute( 'aria-expanded', false );
            mobileMainMenuFocusTrap.deactivate();
          }
        } );
      } );
    }
  };

  /**
   * Handles keyboard navigation for desktop menus using the ArrowRight and ArrowLeft keys.
   *
   * This function adds a keydown event listener to the provided navigation element.
   * It allows users to navigate between menu items using the left and right arrow keys.
   *
   * @param {HTMLElement} navigation - The navigation element to which the keyboard navigation is applied.
   *                                    If the element is null or the view is mobile, the function exits early.
   */
  const handleDesktopMenuKeyboardLeftRight = (navigation) => {
    if (!navigation || mobileView) {
      return;
    }

    navigation.addEventListener( 'keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const currentMenuItem = event.target.closest( '.menu__item' );
        const menuItems = currentMenuItem.parentNode.querySelectorAll( '.menu__item' ) || [];

        const currentIndex = [...menuItems].findIndex( (item) => item === currentMenuItem );
        let targetItem = null;

        if (event.key === 'ArrowRight') {
          if (currentIndex === menuItems.length - 1) {
            targetItem = menuItems[0];
          } else {
            targetItem = menuItems[currentIndex + 1];
          }
        }

        if (event.key === 'ArrowLeft') {
          if (currentIndex === 0) {
            targetItem = menuItems[menuItems.length - 1];
          } else {
            targetItem = menuItems[currentIndex - 1];
          }
        }

        if (targetItem) {
          event.preventDefault();
          const linkOrToggle = targetItem.querySelector( 'a, button' );

          if (linkOrToggle) {
            linkOrToggle.focus();
          } else {
            targetItem.focus();
          }
        }
      }
    } );
  };

  /**
   * Handles keyboard navigation for mobile menus using the ArrowDown and ArrowUp keys.
   *
   * @param navigation - The navigation element to which the keyboard navigation is applied.
   * @param selector - The CSS selector for the menu items to navigate through.
   */
  const handleMobileMenuKeyboardUpDown = (navigation, selector) => {
    if (!navigation || !mobileView) {
      return;
    }

    navigation.addEventListener( 'keydown', event => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const currentMenuItem = event.target.closest( selector );
        const menuItems = navigation.querySelectorAll( selector ) || [];
        const currentIndex = [...menuItems].findIndex( (item) => item === currentMenuItem );
        let targetItem = null;

        if (event.key === 'ArrowDown') {
          if (currentIndex === menuItems.length - 1) {
            targetItem = menuItems[0];
          } else {
            targetItem = menuItems[currentIndex + 1];
          }
        }

        if (event.key === 'ArrowUp') {
          if (currentIndex === 0) {
            targetItem = menuItems[menuItems.length - 1];
          } else {
            targetItem = menuItems[currentIndex - 1];
          }
        }

        if (targetItem) {
          event.preventDefault();
          const linkOrToggle = targetItem.querySelector( 'a, button' );

          if (linkOrToggle) {
            linkOrToggle.focus();
          } else {
            targetItem.focus();
          }
        }
      }
    } );
  };

  /**
   * Updates the mobile submenu headers with the corresponding top-level menu item titles.
   *
   * This function selects all top-level navigation items that have submenus and updates
   * the text of their mobile submenu titles (`.menu__submenu-mobile-title`) to match the
   * text of the top-level menu titles (`.menu__title`).
   *
   * If no top-level navigation items are found, the function exits early.
   */
  const addMobileHeaders = () => {
    const topLevelNavItems = document.querySelectorAll( '.menu__item--has-submenu' );

    if (!topLevelNavItems) {
      return;
    }

    [...topLevelNavItems].forEach( topLevelNavItem => {
      const mobileTitle = topLevelNavItem.querySelectorAll( '.menu__submenu-mobile-title' );
      const topLevelTitle = topLevelNavItem.querySelector( '.menu__title' );
      const topLevelButton = topLevelNavItem.querySelector( '.menu__submenu-toggle-button' );

      if (!topLevelTitle || !mobileTitle || !topLevelButton) {
        return;
      }

      [...mobileTitle].forEach( title => {
        title.innerText = htmlDecode( topLevelTitle.innerHTML.trim() );

        if ('a' === title.tagName.toLowerCase()) {
          title.href = topLevelButton.getAttribute( 'data-href' );
        }
      } );
    } );
  };

  const htmlDecode = (input) => {
    let doc = new DOMParser().parseFromString( input, 'text/html' );
    return doc.documentElement.textContent;
  };

  /**
   * Initializes the navigation menu functionality.
   *
   * This function checks for the presence of required DOM elements and, if they exist,
   * binds the necessary event listeners and logic for both mobile and non-mobile views.
   * It handles dropdown toggles, keyboard navigation for desktop menus, and mobile-specific
   * features such as the mobile menu toggle and submenu headers. Additionally, it listens
   * for window resize events to update the mobile view state.
   */
  const init = () => {
    if (!headerNav || !dropdownToggles || !mobileMenuToggle) {
      return;
    }

    bindDropdownToggles();

    // Non-mobile exclusive.
    [
      headerNav,
      headerCtasDesktop,
      topbarMenuLeftDesktop,
      topbarMenuRightDesktop,
    ].forEach( handleDesktopMenuKeyboardLeftRight );

    // Mobile exclusive.
    handleMobileMenuKeyboardUpDown( headerNavWrap, '.menu__item' );
    bindMobileMenuToggle();
    addMobileHeaders();

    // Check if the mobile menu toggle is visible on resize, this will activate specific mobile view logic when resizing the window.
    window.addEventListener( 'resize', () => {
      mobileView = window.getComputedStyle( mobileMenuToggle ).display !== 'none';
    } );
  };

  /**
   * Initializes the menu component.
   */
  init();
};

export default menu;

